/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSignOut';

const ActionSignOutManager: ActionManagerFn<'ActionSignOut'> = (
  platform: Platform,
): ActionManager<'ActionSignOut'> => {
  return {
    ...baseActionManager(platform, 'ActionSignOut'),
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'Sign Out',
    icon: { iconName: 'sign-out' },
    description: `**Overview**<br/>
The Sign Out action is a critical security and session management component designed to terminate the current user's active session. When executed, it invalidates the authentication tokens associated with the user, effectively "logging them out" of the application.<br/><br/>
This component is the standard implementation for the "Log Out" or "Sign Out" button found in application headers or profile menus. It ensures that subsequent access attempts require re-authentication, protecting user data—especially in environments where devices might be shared.

**Key Features**<br/>
* **Session Termination:** Securely ends the user's interaction with the platform by clearing authentication states and tokens.
* **Redirect Behavior:** Typically triggers an automatic redirection to the application's public login page or landing screen once the session is destroyed.
* **Zero Configuration:** Inherits all properties directly from the base action structure, requiring no additional parameters or target IDs to function; it inherently targets the currently authenticated user.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **User Navigation:** The primary action attached to the "Log Out" link in the user profile dropdown menu.
* **Security Workflows:** Automatically signing a user out as part of a "Change Password" workflow to force them to log in again with their new credentials.
* **Session Timeout:** Used as the trigger for a manual logout button on a "Session Expired" warning modal.`,
  };
};

export default ActionSignOutManager;
